<template>
  <div :id="comment.id" class="gmf-comment gmf-comment-v2" :class="{'gmf-context': addGmfContextClass, 'gmf-comment-reply': depth > 0, 'gmf-comment-final': finalNode, 'gmf-comment-collapse': collapse, 'gmf-comment-translated': isTranslated}">
    <div class="gmf-comment-indent"
      v-for="i in depth"
      :key="i"
      :class="{
        'gmf-comment-indent-nested': i > 1,
        'gmf-comment-branch': parentBranches && parentBranches[i-1],
        'gmf-comment-leaf': i === depth,
      }"
    />
    <div class="gmf-comment-customer-col" v-if="elementsToShow.avatar && leftColumnAvatar">
      <Avatar :animated="elementsToShow.animateAvatar" :customer="comment.author" defaultPopupTab="comments" :disablePopup="disableProfilePopup" :view="avatarView || 'headshot'" />
      <div class="gmf-comment-stem" v-if="elementsToShow.stem && (comment.replyCount > 0 || isReplying)" :class="{'gmf-comment-stem-nested': depth > 0}" ></div>
    </div>
    <div class="gmf-comment-content-col">
      <div class="gmf-comment-content">
        <div class="gmf-comment-content-title">
          <div class="gmf-comment-customer-inline" v-if="elementsToShow.avatar && ! leftColumnAvatar">
            <Avatar disablePopout :animated="elementsToShow.animateAvatar" :customer="comment.author" defaultPopupTab="comments" :disablePopup="disableProfilePopup" :view="avatarView || 'headshot'" />
          </div>
          <div class="gmf-comment-header">
            <div class="gmf-comment-header-inline">
              <CustomerName class="gmf-comment-name" :customer="comment.author" defaultPopupTab="comments" :disablePopup="disableProfilePopup" />
              <div class="gmf-commenter-status gmf-text-small" v-if="description" v-text="description"></div>
              <span v-else-if="elementsToShow.action && comment.target && comment.target.name && comment.target.name !== '<N/A>'" class="gmf-commenter-status gmf-text-small">
                {{ actionPhrase }}
                <a v-if="comment.target.type === 'board' && ! disableTargetLink" v-text="comment.target.name" href="#" @click.stop.prevent="openSocialBoard(comment.target.id)" />
                <Link v-else-if="comment.target.url && ! disableTargetLink" v-text="comment.target.name" :href="comment.target.url" />
                <span v-else v-text="comment.target.name" />
              </span>
              <span v-else-if="elementsToShow.action" class="gmf-commenter-status gmf-text-small">{{t('action_phrase_product.' + commentType ) || t('action_phrase_product.product_comment' )}}</span>
              <span v-else-if="comment.isVerified" class="gmf-comment-verification gmf-text-small"><div class="gmf-comment-verification-icon"><Icon icon="verified-comment-checkmark" /></div><span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{t('verified_buyer')}}</span></span>
            </div>
            <CustomerLevelRow v-if="showLevel" :customer="comment.author" />
            <CustomerAttributePills v-if="! collapse && customerAttributes && elementsToShow.customerAttributes" :attributes="customerAttributes" location="comment" :maxLines="1" />
          </div>
        </div>
        <div class="gmf-comment-body">
          <div class="gmf-comment-body-image" v-if="targetImage && elementsToShow.targetImage" :style="'background-image: url('+targetImage+')'">
          </div>
          <div class="gmf-comment-body-content">
            <div v-if="ratingPlacement === 'above'" class="gmf-mb-2">
              <div class="gmf-comment-target gmf-text-xs" v-if="elementsToShow.target && comment.target && comment.target.name && ~~depth <= 0 && comment.target.name != '<N/A>'" v-text="comment.target.name"></div>
              <RatingStars v-if="comment.content.ratings.product_score" type="product_score" :rating="comment.content.ratings.product_score.value"/>
              <RatingStars v-else-if="comment.content.ratings.hype_factor" type="hype_factor" :rating="comment.content.ratings.hype_factor.value"/>
              <RatingStars v-else-if="comment.content.ratings.site_score" type="site_score" :rating="comment.content.ratings.site_score.value"/>
            </div>

            <div class="gmf-comment-title gmf-subheader" v-if="title && elementsToShow.title">{{ title }}</div>
            <!-- @TODO "Visa mer" om text är större än X -->

            <div class="gmf-comment-text" v-if="text && text.length > 0" ref="text">
              <CommentText :key="translate" :value="text" :maxLines="collapse ? 3 : null"></CommentText>
            </div>

            <div class="gmf-comment-pros-and-cons" v-if="hasProsOrCons && elementsToShow.prosAndCons">
              <div class="gmf-comment-pros" v-if="pros && pros.length > 0">
                <div class="gmf-comment-pc" v-for="pro in pros" :key="pro">
                  <Icon icon="pro" />
                  <div class="gmf-comment-pc-text" v-text="pro"></div>
                </div>
              </div>
              <div class="gmf-comment-cons" v-if="cons && cons.length > 0">
                <div class="gmf-comment-pc" v-for="con in cons" :key="con">
                  <Icon icon="con" />
                  <div class="gmf-comment-pc-text" v-text="con"></div>
                </div>
              </div>
            </div>

            <div v-if="shouldTranslate && elementsToShow.translateButton" class="gmf-translate-btn">
              <a class="gmf-link" @click.stop.prevent="translate = !translate" href="#">{{ !translate ?
                t('translations.translate') :
                t('translations.show_original') }}</a>
            </div>

            <div class="gmf-comment-text-rating gmf-text-xs" v-if="comment.content.ratings.perceived_size">
              {{ t('perceived_size.label') }}: {{ t('perceived_size.' + ({1: 'small', 2: 'normal', 3: 'large'})[comment.content.ratings.perceived_size.value]) }}
            </div>

            <CommentImageGallery v-if="comment.content.images && comment.content.images.length > 0 && elementsToShow.uploads" :images="comment.content.images" :clickHandler="(index) => handleClick(index)"/>
            <div class="gmf-comment-body-footer">
              <template v-if="ratingPlacement === 'below'">
                <div class="gmf-comment-target gmf-text-xs" v-if="elementsToShow.target && comment.target && comment.target.name && ~~depth <= 0 && comment.target.name != '<N/A>'" v-text="comment.target.name"></div>
                <RatingStars v-if="comment.content.ratings.product_score" type="product_score" :rating="comment.content.ratings.product_score.value"/>
                <RatingStars v-else-if="comment.content.ratings.hype_factor" type="hype_factor" :rating="comment.content.ratings.hype_factor.value"/>
                <RatingStars v-else-if="comment.content.ratings.site_score" type="site_score" :rating="comment.content.ratings.site_score.value"/>
              </template>

              <div class="gmf-comment-date gmf-text-xs" v-if="!hideDates">{{ createdAt }}</div>
            </div>

            <!-- @TODO using sp classes here is not super clean, but the styling works for now -->
            <div class="gmf-sp-linked-products-container" v-if="comment.linkedProducts && comment.linkedProducts.length > 0">
              <div class="gmf-subheader" v-text="t('linked_products_header')"></div>
              <div class="gmf-sp-linked-products-list">
                <Link class="gmf-sp-linked-product" v-for="product in comment.linkedProducts" :key="product.id" :href="product.url">
                  <div class="gmf-sp-linked-product-image" :style="{backgroundImage: 'url(' + product.imageUrl + ')'}"></div>
                  <div class="gmf-sp-linked-product-title">{{ product.name }}</div>
                </Link>
              </div>
            </div>
          </div>          
        </div>
        <div class="gmf-comment-footer" v-if="elementsToShow.footer && isLinkedComment">
          <span class="gmf-text-xs gmf-ml-3">{{ originatedFrom }}</span>
        </div>
        <div class="gmf-comment-footer" v-else-if="elementsToShow.footer && (canLike || canReply || canDelete)">
          <CommentVoteButtons :comment="comment" v-if="canLike" />
          <button class="gmf-comment-reply-button gmf-comment-footer-btn" v-if="canReply && elementsToShow.reply" @click.stop="reply">
            <Icon class="gmf-comment-reply-icon" icon="reply" /> {{t('reply')}}
          </button>
          <button class="gmf-comment-delete-button gmf-comment-footer-btn gmf-right" v-if="canDelete" @click.stop="deleteComment(comment)">
            <Icon class="gmf-comment-delete-icon" icon="delete"/> {{t('delete')}}
          </button>
          <button class="gmf-comment-report-button gmf-comment-footer-btn gmf-right" v-else-if="!isYourself && reportingEnabled" @click.stop="openReportModal(comment)">
            {{ t('report_post') }}
          </button>
        </div>
      </div>
    </div>
    <div class="gmf-clearfix" />
  </div>
  <CommentReplyForm
    v-if="isReplying && ! hideReplies"
    :depth="depth+1"
    :finalNode="comment.replies.length === 0"
    :parentBranches="[...(parentBranches || []), comment.replyCount > 0]"
    :commentType="commentType"
    :targetId="comment.target ? comment.target.id : targetId"
    :parentId="comment.id"
  />
  <template v-if="showCompactReplies && ! hideReplies">
    <CommentOneliner
      v-for="(reply, index) in comment.replies"
      :key="reply.id"
      :comment="reply"
      :depth="depth+1"
      :finalNode="index === comment.replyCount - 1"
      :parentBranches="[...(parentBranches || []), index !== comment.replyCount - 1]"
      @expand="uncompact"
    />
    <div v-if="comment.replyCount > comment.replies.length && ! hideReplies" class="gmf-comment gmf-comment-more-replies" :class="{'gmf-comment-reply': depth > 0, 'gmf-comment-final': finalNode}">
      <div class="gmf-comment-indent"
        v-for="i in depth + 1"
        :key="i"
        :class="{
          'gmf-comment-indent-nested': i > 1,
          'gmf-comment-branch': parentBranches && parentBranches[i-1],
          'gmf-comment-leaf': i === depth + 1,
        }"
      />
      <div class="gmf-comment-content-col">
        <a href="#" @click.prevent="uncompact">Visa {{ comment.replyCount - comment.replies.length }} svar till ...</a>
      </div>
    </div>
  </template>
  <template v-else-if="comment.replyCount > 0 && ! hideReplies">
    <Comment
      v-for="(reply, index) in comment.replies"
      :key="reply.id"
      :comment="reply"
      :depth="depth+1"
      :finalNode="index === comment.replyCount - 1"
      :parentBranches="[...(parentBranches || []), index !== comment.replyCount - 1]"
      :voteMode="voteMode"
      :showDelete="showDelete"
      :targetId="targetId"
      :deleteMethod="deleteMethod"
      @loadReplies="id => $emit('loadReplies', id)"
    />
    <div v-if="comment.replyCount > comment.replies.length && ! hideReplies" class="gmf-comment gmf-comment-more-replies" :class="{'gmf-comment-reply': depth > 0, 'gmf-comment-final': finalNode}">
      <div class="gmf-comment-indent"
        v-for="i in depth + 1"
        :key="i"
        :class="{
          'gmf-comment-indent-nested': i > 1,
          'gmf-comment-branch': parentBranches && parentBranches[i-1],
          'gmf-comment-leaf': i === depth + 1,
        }"
      />
      <div class="gmf-comment-content-col">
        <a href="#" @click.prevent="$emit('loadReplies', comment.id)">Visa {{ comment.replyCount - comment.replies.length }} svar till ...</a>
      </div>
    </div>
  </template>
</template>

<style>
.gmf-comment-customer-col {
  width: 64px;
  flex-shrink: 0;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  margin-right: 6px;
}
.gmf-comment-reply .gmf-comment-customer-col {
  width: 50px;
}

.gmf-comment-content-title {
  gap: 2px;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}
.gmf-comment-customer-inline {
  width: 48px;
  flex-shrink: 0;
  padding-top: 0px;
  align-self: flex-end;
}
.gmf-comment-customer-inline:has(.gmf-avatar-3d) {
  width: 64px;
  margin-left: 4px;
}
.gmf-comment-customer-inline:has(.gmf-avatar-3d) .gmf-avatar {
  background: none !important;
}
.gmf-comment-customer-inline:has(.gmf-avatar-3d) .gmf-avatar-content {
  border-radius: 0px;
  margin-top: 2px;
}

.gmf-comment-content-col {
  flex-basis: 0;
  min-width: 0;
  flex-grow: 1;
  margin-top: var(--gmf-p-3);
  margin-bottom: var(--gmf-p-3);
}
.gmf-comment-avatar {
  width: 100%;
  border-radius: 100%;
  margin-top: 0px;
  background-size: 100%;
  background-position: center;
  background-repeat: no-repeat;
}
.gmf-comment-avatar::after {
  width: 100%;
  padding-bottom: 100%;
  content: "";
  display: block;
}
.gmf-comment-body {
  background: var(--gmf-comments-bg);
  padding: 12px 16px;
  border-radius: var(--gmf-rounding);
  position: relative;
  font-size: 14px;
  display: inline-block;
  max-width: 100%;
  min-width: 100%;
  color: var(--gmf-col-black);

  display: flex;
  gap: var(--gmf-p-4);
}
.gmf-comment {
  display: flex;
}
.gmf-comment-title {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 8px;
  color: var(--gmf-col-black);
}
.gmf-comment-description {
  font-size: 11px;
  padding-left: 6px;
  color: var(--gmf-col-darkgray);
}
.gmf-comment-content {
  display: inline-block;
  position: relative;
  max-width: 100%;
}
.gmf-comment-footer {
  user-select: none;
  margin-top: 2px;
}
.gmf-comment-body-content {
  flex-grow: 1;
  max-width: 100%;
  overflow: hidden;
  gap: var(--gmf-p-2);
}
.gmf-comment-date, .gmf-context .gmf-comment-date {
  margin-left: 12px;

  text-align: right;
  flex-grow: 1;
}

.gmf-comment-target, .gmf-context .gmf-comment-target {
  width: 100%;
  padding-bottom: 4px;
}

.gmf-comment-voting {
  display: inline-block;
  margin-right: 16px;
}

.gmf-comment-body-footer {
  clear: both;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-top: auto;
}

.gmf-comment-body-image {
  width: 55px;
  height: 55px;
  border-radius: var(--gmf-p-3);
  max-width: unset;
  max-height: unset;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  flex-grow: 0;
  background-color: white;
}

.gmf-comment-name, .gmf-context .gmf-comment-name {
  font-size: 14px;
  font-weight: bold;
  color: var(--gmf-col-black);
  margin-top: 0;
  margin-bottom: 0px;
  display: inline-block;
  white-space: nowrap;
}
.gmf-comment-header {
  margin-left: var(--gmf-p-2);
  gap: 2px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.gmf-comment-header-inline {
  gap: 4px;
  display: flex;
  align-items: flex-end;
}
.gmf-comment-vote-score {
  margin-left: 6px;
  margin-right: 6px;
  font-weight: bold;
  display: inline-block;
  text-align: center;
  color: var(--gmf-col-darkgray);
}

.gmf-comment-footer-btn, .gmf-context .gmf-comment-footer-btn {
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-flex;
  cursor: pointer;
  font-size: var(--gmf-default-font-size);
  color: var(--gmf-col-darkgray);
  white-space: nowrap;

  align-items: center;
  justify-content: center;
  gap: var(--gmf-p-2);

  /* button reset */
  border: none;
  margin: 0;
  background: none;
}

.gmf-comment-footer-btn:hover {
  background-color: var(--gmf-col-lightgray);
  text-decoration: none;
}

.gmf-comment-upvote, .gmf-comment-downvote, .gmf-comment-reply-icon, .gmf-comment-delete-icon, .gmf-comment-like-icon {
  cursor: pointer; /*@TODO needed?*/
}

.gmf-commenter-status a {
  text-decoration: none;
  font-weight: bold;
}
.gmf-comment-text {
  display: flex;
  flex-direction: column;
  gap: 10px;
  word-wrap: break-word;
}
.gmf-comment-report-button {
  color: var(--gmf-col-darkgray);
}
.gmf-comment-report-button:hover {
  text-decoration: underline;
}
.gmf-commenter-status {
  display: inline;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.gmf-comment-verification {
  display: inline-flex;
  gap: var(--gmf-p-2);
  align-items: center;
  overflow: hidden;
}
.gmf-comment-verification-icon {
  line-height: 0 !important;
  width: 12px;
  height: 12px;
  display: inline-block;
  background: var(--gmf-col-verified);
  --gmf-icon-color: var(--gmf-col-white);
  border-radius: 99px;
  position: relative;
  flex-shrink: 0;
}
.gmf-comment-verification-icon .gmf-icon {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.gmf-comment-pros-and-cons {
  display: flex;
  flex-wrap: wrap;
  margin-top: 0px;
  margin-bottom:  10px;
  overflow: hidden;
}

.gmf-comment-pros, .gmf-comment-cons {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: var(--gmf-p-3);
}

.gmf-comment-text + .gmf-comment-pros-and-cons {
  margin-top: 10px;
  gap: var(--gmf-p-3);
}

.gmf-comment-pc {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--gmf-p-2);
}
.gmf-comment-pros {
  --gmf-icon-color: var(--gmf-comment-pro-col);
}
.gmf-comment-cons {
  --gmf-icon-color: var(--gmf-comment-con-col);
}

.gmf-comment-pc .gmf-icon {
  width: 17px;
  height: 17px;
  flex-grow: 0;
  flex-shrink: 0;
}

.gmf-comment-stem {
  margin-left: 30px;
  margin-right: 8px;
  margin-top: 3px;
  flex-shrink: 0;
  border-left: 3px solid var(--gmf-comment-indent-col);
  width: 12px;
  flex-grow: 1;
}
.gmf-comment-indent {
  margin-left: 15px;
  margin-right: 4px;
  flex-shrink: 0;
  width: 0;
  width: 8px;
  position: relative;
  border-left: 3px solid transparent;
}
.gmf-comment-branch {
  border-left: 3px solid var(--gmf-comment-indent-col);
}
.gmf-comment-leaf::after {
  position: absolute;
  display: block;
  content: "";
  left: -3px;
  top: 0;
  border-left: 3px solid var(--gmf-comment-indent-col);
  border-bottom: 3px solid var(--gmf-comment-indent-col);
  width: 12px;
  height: 38px;
  border-radius: 0px 0px 0px 8px;
}

.gmf-comment-indent-nested, .gmf-comment-stem-nested {
  margin-left: 15px;
  margin-right: 4px;
}

@media (max-width: 480px) {
  .gmf-comment-body-image {
    width: 48px;
    height: 48px;
  }
  .gmf-comment-reply .gmf-comment-customer-col {
    width: 40px;
  }
  .gmf-comment-body {
    padding: 10px 14px;
    font-size: 12px;
  }
  .gmf-comment-pros div::after, .gmf-comment-cons div::after {
    top: 1px;
  }

  .gmf-comment-date {
    font-size: 10px;
  }
}

.gmf-comment-more-replies .gmf-comment-leaf::after {
  height: 10px;
}
.gmf-comment-more-replies a {
  font-weight: bold;
}
.gmf-comment-more-replies .gmf-comment-content-col {
  margin-top: var(--gmf-p-2);
}
.gmf-comment-v2 .gmf-comment-name.gmf-link-disabled {
  color: var(--gmf-col-darkgray);
}
.gmf-translate-btn {
  margin-top: var(--gmf-p-4); 
  margin-bottom: var(--gmf-p-3);
}
</style>

<script>
/* eslint-disable vue/no-mutating-props */

import state from '@/state';

import modal from '@/utils/modal';
import timeAgo from '@/utils/timeAgo';
import advancedString from '@/utils/advancedString';

import Icon from '@/components/Icon';
import Avatar from '@/components/Avatar';
import CustomerName from '@/components/CustomerName';
import CustomerLevelRow from '@/components/CustomerLevelRow';
import CommentVoteButtons from '@/components/CommentVoteButtons';

import CommentOneliner from './CommentOneliner';
import CommentReplyForm from './CommentReplyForm';
import CommentImageGallery from './CommentImageGallery';
import CustomerAttributePills from '@/components/CustomerAttributePills';
import CommentText from './CommentText';

import openSocialModal from '@/widgets/SocialFeed/openSocialModal';
import Link from '@/components/Link.vue';

import RatingStars from '@/components/RatingStars';

import openPostModal from '@/widgets/PostModal/openPostModal';
import openReportModal from '@/components/ReportModal/openReportModal';

// import openCommentFormV2 from '@/components/CommentFormV2';

const t = state.t.prefix('comments');

export default {
  name: 'Comment',
  props: [
    'addGmfContextClass', // @TODO ugly hack, we should auto add something like gmf-context to the outer div of every widget
    'hideReplies',
    'collapse',
    'comment',
    'depth',
    'finalNode',
    'parentBranches',
    'voteMode',
    'showDelete',
    'deleteMethod',
    'targetId',
    'description',
    'elements',
    'expandReplies',
    'disableProfilePopup',
    'disableTargetLink',
    'avatarView', // @TODO default headshot
    'disableReplies',
    'inPostModal',
    'leftColumnAvatar'
  ],
  components: {
    Icon,
    Avatar,
    CustomerName,
    CommentOneliner,
    CustomerLevelRow,
    CommentReplyForm,
    CommentVoteButtons,
    CommentImageGallery,
    CustomerAttributePills,
    CommentText,
    Link,
    RatingStars,
  },
  emits: ['loadReplies', 'selectedComment'],
  data () {
    return {
      showCompactReplies: this.comment.replies.length > 0 && ! this.expandReplies,
      state,
      isReplying: false,
      isCompacted: false,
      translate: state.merchantConfig.widgets?.translations?.mode === 'autoTranslateAndOriginalButton',
    };
  },
  computed: {
    originatedFrom () {
      return advancedString(t('originated_from'), {
        merchantName: this.comment.merchant.name,
      });
    },
    isLinkedComment () {
      // Returns true if the comment belongs to a different merchant
      return this.comment.merchant && this.comment.merchant.id != this.state.merchantId
    },
    customerAttributes () {
      if (! this.comment.author.attributesJson) {
        return null;
      }
      return JSON.parse(this.comment.author.attributesJson);
    },
    targetImage () {
      if (this.comment.content.images && this.comment.content.images[0]) {
        return this.comment.content.images[0].url;
      }
      if (this.comment.target && this.comment.target.imageUrl) {
        return this.comment.target.imageUrl;
      }
      return null;
    },
    showLevel () {
      return this.elementsToShow.customerLevel;
    },
    actionPhrase () {
      return t('action_phrase.' + this.commentType)
    },
    elementsToShow () {
      const toShow = {
        title: true,
        avatar: true,
        uploads: true,
        prosAndCons: true,
        action: false,
        productImage: false,
        targetImage: false,
        target: true,
        footer: true,
        stem: true,
        customerLevel: true,
        reply: true,
        customerAttributes: true,
        animateAvatar: false,
        translateButton: true,
        date: true,
      };
      if (this.elements) {
        for (const key in this.elements) {
          toShow[key] = this.elements[key];
        }
      }

      return toShow;
    },
    isLoggedIn () {
      return !! this.profile;
    },
    commentType () {
      return this.comment.type.name;
    },
    ratingPlacement () {
      return this.state.merchantConfig?.widgets?.comments?.ratingPlacement || 'below';
    },
    commentTypeConfig () {
      const c = this.state.merchantConfig?.comments || {};
      return c[this.commentType] || {};
    },
    canReply () {
      if (! this.isLoggedIn) {
        return false;
      }
      if (! this.commentTypeConfig.allowReplies) {
        return false;
      }
      if (this.depth > 0) {
        return this.commentTypeConfig.allowNestedReplies;
      }
      return true
    },
    createdAt () {
      let d = new Date(this.comment.createdAt);
      return timeAgo(d.getTime()/1000);
    },
    isTranslated () {
      return this.comment?.translation && this.translate;
    },
    title() {
      if (this.comment && this.comment?.translation && this.translate) {
        return this.comment.translation.title;
      }

      return this.comment.content.title;
    },
    text() {
      if (this.comment && this.comment?.translation && this.translate) {
        return this.comment.translation.text;
      }

      return this.comment.content.text;
    },
    pros() {
      if (this.comment && this.comment?.translation && this.translate) {
        return this.comment.translation.pros;
      }

      return this.comment.content.pros;
    },
    cons() {
      if (this.comment && this.comment?.translation && this.translate) {
        return this.comment.translation.cons;
      }

      return this.comment.content.cons;
    },
    hasProsOrCons () {
      return (this.pros && this.pros.length > 0) || (this.cons && this.cons.length > 0);
    },
    pluralizedLikes () {
      if (Math.abs(this.comment.votes.score) === 1) {
        return t('like');
      }
      return t('likes');
    },
    profile() {
      return this.state.profile;
    },
    canLike() {
      const content = this.comment.content;

      if (content.title) return true;
      if (content.text ) return true;
      if (this.hasProsOrCons) return true;
      if (content.images && content.images.length > 0) return true;

      return false;
    },
    isYourself() {
      return this.comment.author.id === (this.profile || {}).id;
    },
    canDelete() {
      if (!this.showDelete) return false;
      return this.isYourself;
    },
    translateMode(){
     return this.state.merchantConfig.widgets?.translations?.mode; 
    },
    shouldTranslate() {
      if (this.translateMode === 'translateNothing') {
        return false;
      }

      if (this.comment.language === 'und') {
        return false;
      }

      if (!this.comment.translation) {
        return false;
      }

      return this.comment.translation.language !== this.comment.language;
    },
    hideDates() {
      if (! this.elementsToShow.date) {
        return true;
      }
      return this.state.merchantConfig?.widgets?.comments?.hideDates;
    },
    reportingEnabled() {
      return this.state.merchantConfig?.widgets?.comments?.reporting?.enabled;
    },
  },
  methods: {
    openPostModal,
    openReportModal,

    handleClick (index) {
      if (this.inPostModal) {
        this.$emit('selectedComment', this.comment, index);
      } else {
        if (! this.isLinkedComment) {
          openPostModal(this.comment.id, index);
        }
      }
    },
    openSocialBoard (boardId) {
      openSocialModal({
        scrollToCommentId: this.comment.id,
        displayHeader: true,
        filter: { board_id: boardId }
      });
    },
    uncompact () {
      this.showCompactReplies = false;
      this.$emit('loadReplies', this.comment.id);
    },
    t,
    deleteComment(comment) {
      modal.confirmDelete({
        title: t("should_delete"),
        description: t("delete_modal_description"),
      }, () => {
        if (this.deleteMethod) {
          this.deleteMethod(comment);
        }
      });
    },
    reply () {
      /*
      @TODO doesn't have reasonable phrases for replies
      if (this.state.merchantConfig.commentForm.useV2CommentForm) {
        openCommentFormV2(this.comment.type.name, this.comment.target.id, this.comment.id);
        return;
      }
      */
      this.isReplying = true;
    },
  },
};
</script>
